import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { collection, doc, getDoc, getDocs, limit, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { UI, denseInputSx, textButtonSx } from './settingsUi';
import { toPhone10 } from '../../utils/whatsappWorkingHours';

const STATE_COLLECTION = 'whatsappContactState';

type FilterKey = 'optedOut' | 'botPaused' | 'needsHuman';

const FILTERS: Array<{ key: FilterKey; label: string; empty: string }> = [
  { key: 'optedOut', label: 'Opted out', empty: 'Nobody has opted out.' },
  { key: 'botPaused', label: 'Bot paused', empty: 'Auto-replies are not paused for anyone.' },
  { key: 'needsHuman', label: 'Asked for the team', empty: 'No open "Talk to our team" requests.' },
];

interface ContactRow {
  phone10: string;
  data: Record<string, any>;
  leadName?: string | null;
}

const formatDate = (value: any) =>
  value?.toDate ? value.toDate().toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '';

const unavailableUntil = (data: Record<string, any>) => {
  const date = data.whatsappUnavailableUntil?.toDate?.();
  return date instanceof Date && date.getTime() > Date.now() ? date : null;
};

const HubContactsTab = () => {
  const { currentUser } = useAuth();
  const [filter, setFilter] = useState<FilterKey>('optedOut');
  const [rows, setRows] = useState<ContactRow[] | null>(null);
  const [counts, setCounts] = useState<Record<FilterKey, number>>({ optedOut: 0, botPaused: 0, needsHuman: 0 });
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<ContactRow | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    const unsubs = FILTERS.map(({ key }) =>
      onSnapshot(
        query(collection(db, STATE_COLLECTION), where(key, '==', true), limit(500)),
        async (snap) => {
          setCounts((prev) => ({ ...prev, [key]: snap.size }));
          if (key !== filter) return;
          const list: ContactRow[] = snap.docs.map((d) => ({ phone10: d.id, data: d.data() }));
          // Lead names for the visible list.
          await Promise.all(
            list.slice(0, 100).map(async (row) => {
              const leads = await getDocs(query(collection(db, 'leads'), where('phone10', '==', row.phone10), limit(1))).catch(() => null);
              row.leadName = leads && !leads.empty ? String(leads.docs[0].data().name || '') : null;
            })
          );
          setRows(list);
        },
        () => setRows([])
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [filter]);

  const update = async (phone10: string, patch: Record<string, unknown>, text: string) => {
    try {
      await setDoc(
        doc(db, STATE_COLLECTION, phone10),
        { phone10, ...patch, updatedAt: serverTimestamp(), updatedByEmail: currentUser?.email || null },
        { merge: true }
      );
      setNotice({ type: 'success', text });
      if (found?.phone10 === phone10) setFound({ ...found, data: { ...found.data, ...patch } });
    } catch (err: any) {
      setNotice({ type: 'error', text: `Could not update: ${err?.message || err}` });
    }
  };

  const lookup = async () => {
    const phone10 = toPhone10(search);
    if (!phone10) {
      setNotice({ type: 'error', text: 'Enter a 10-digit mobile number.' });
      return;
    }
    const snap = await getDoc(doc(db, STATE_COLLECTION, phone10));
    const leads = await getDocs(query(collection(db, 'leads'), where('phone10', '==', phone10), limit(1)));
    setFound({
      phone10,
      data: snap.exists() ? snap.data() : {},
      leadName: leads.empty ? null : String(leads.docs[0].data().name || ''),
    });
    setNotice(null);
  };

  const actions = (row: ContactRow) => {
    const d = row.data;
    return (
      <Stack direction="row" gap={0.75} flexWrap="wrap">
        {d.optedOut ? (
          <Button size="small" variant="outlined" sx={textButtonSx} onClick={() => update(row.phone10, { optedOut: false, optedInAt: serverTimestamp() }, `+91 ${row.phone10} can get messages again.`)}>
            Opt back in
          </Button>
        ) : (
          <Button size="small" variant="outlined" sx={textButtonSx} onClick={() => update(row.phone10, { optedOut: true, optedOutAt: serverTimestamp() }, `+91 ${row.phone10} is opted out.`)}>
            Opt out
          </Button>
        )}
        {d.botPaused ? (
          <Button size="small" variant="outlined" sx={textButtonSx} onClick={() => update(row.phone10, { botPaused: false }, `Auto-replies resumed for +91 ${row.phone10}.`)}>
            Resume bot
          </Button>
        ) : (
          <Button size="small" variant="outlined" sx={textButtonSx} onClick={() => update(row.phone10, { botPaused: true, botPausedAt: serverTimestamp() }, `Auto-replies paused for +91 ${row.phone10}.`)}>
            Pause bot
          </Button>
        )}
        {d.needsHuman && (
          <Button size="small" variant="outlined" sx={textButtonSx} onClick={() => update(row.phone10, { needsHuman: false }, 'Marked as handled.')}>
            Mark handled
          </Button>
        )}
      </Stack>
    );
  };

  const current = FILTERS.find((f) => f.key === filter)!;

  return (
    <Box sx={{ bgcolor: UI.surface, border: `1px solid ${UI.line}`, borderRadius: 2.5 }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${UI.line}` }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: UI.ink }}>Contacts & opt-in</Typography>
          <Typography sx={{ fontSize: 12, color: UI.muted }}>
            Customers who message you have opted in to replies. STOP opts them out of every automatic message.
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Find a number"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') lookup();
          }}
          inputProps={{ 'aria-label': 'Find a number', inputMode: 'tel' }}
          sx={{ ...denseInputSx, width: 180 }}
        />
        <Button size="small" variant="outlined" onClick={lookup} sx={textButtonSx}>
          Find
        </Button>
      </Stack>

      {notice && (
        <Alert severity={notice.type} onClose={() => setNotice(null)} sx={{ borderRadius: 0, fontSize: 12.5, py: 0 }}>
          {notice.text}
        </Alert>
      )}

      {found && (
        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ px: 2, py: 1.25, bgcolor: UI.goldTint, borderBottom: `1px solid ${UI.line}` }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: UI.ink }}>
              +91 {found.phone10} {found.leadName ? `· ${found.leadName}` : '· not a lead'}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: UI.muted }}>
              {found.data.optedOut ? 'Opted out' : unavailableUntil(found.data)
                ? `WhatsApp temporarily unavailable until ${formatDate(found.data.whatsappUnavailableUntil)}`
                : 'Can get messages'} · {found.data.botPaused ? 'bot paused' : 'bot active'}
              {found.data.lastAutoReplyAt ? ` · last auto-reply ${formatDate(found.data.lastAutoReplyAt)}` : ''}
            </Typography>
          </Box>
          {actions(found)}
        </Stack>
      )}

      <Stack direction="row" gap={0.5} sx={{ px: 2, pt: 1.25 }}>
        {FILTERS.map((f) => (
          <Box
            key={f.key}
            component="button"
            type="button"
            onClick={() => {
              setRows(null);
              setFilter(f.key);
            }}
            sx={{
              fontFamily: UI.body,
              fontSize: 12.5,
              fontWeight: filter === f.key ? 600 : 500,
              border: `1px solid ${filter === f.key ? UI.gold : UI.line}`,
              bgcolor: filter === f.key ? UI.goldTint : 'transparent',
              color: filter === f.key ? UI.gold : UI.ink,
              borderRadius: 999,
              px: 1.25,
              height: 28,
              cursor: 'pointer',
            }}
          >
            {f.label} · {counts[f.key]}
          </Box>
        ))}
      </Stack>

      <Box sx={{ px: 2, py: 1.25 }}>
        {rows === null ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={20} sx={{ color: UI.gold }} />
          </Stack>
        ) : rows.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: UI.muted, py: 2 }}>{current.empty}</Typography>
        ) : (
          <Box sx={{ border: `1px solid ${UI.line}`, borderRadius: 1.5 }}>
            {rows.map((row, idx) => (
              <Stack key={row.phone10} direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ px: 1.5, py: 1, borderTop: idx ? `1px solid ${UI.line}` : 'none' }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography sx={{ fontSize: 13, color: UI.ink, fontVariantNumeric: 'tabular-nums' }}>
                    +91 {row.phone10} {row.leadName ? <Box component="span" sx={{ color: UI.muted }}>· {row.leadName}</Box> : null}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: UI.muted }}>
                    {filter === 'optedOut' && `${row.data.optOutReason === 'wrong-number' ? 'Wrong number (from a call)' : 'Opted out'} ${formatDate(row.data.optedOutAt)}`}
                    {filter === 'botPaused' && `Paused ${formatDate(row.data.botPausedAt)}`}
                    {filter === 'needsHuman' && `Asked ${formatDate(row.data.needsHumanAt)}`}
                  </Typography>
                </Box>
                {actions(row)}
              </Stack>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default HubContactsTab;
